import type { ReactNode } from "react";
import { useBookCta } from "@/hooks/use-book-href";
import { KUALOA_STAY_PATH } from "@shared/property-context";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import BrandLockup from "@/components/layout/brand-lockup";
import { ContactActionLinks } from "@/components/contact/contact-action-links";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import type { SiteInfoSettings } from "@/lib/seo-utils";
import { Loader2, CheckCircle2 } from "lucide-react";

function FooterLink({
  href,
  testId,
  children,
}: {
  href: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      className="text-[15px] text-ink-soft hover:text-ink transition-colors font-sans block py-2"
      data-testid={testId}
    >
      {children}
    </Link>
  );
}

export function FooterNewsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          consent: true,
        }),
      });
      if (!res.ok || (await res.json()).accepted !== true) {
        setStatus("error");
        return;
      }
      setStatus("done");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="mt-8 bg-sand border border-sand-deep rounded-2xl p-6 md:p-7">
        <h3 className="font-sans font-normal text-[11px] tracking-[0.3em] uppercase text-forest mb-2">Stay in touch</h3>
        <p role="status" className="text-[15px] font-sans text-ink-soft inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-palm" aria-hidden="true" /> Thanks. Your signup request was processed. Existing email opt-outs remain unchanged.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 bg-sand border border-sand-deep rounded-2xl p-6 md:p-7">
      <h3 className="font-sans font-normal text-[11px] tracking-[0.3em] uppercase text-forest mb-3">Stay in touch</h3>
      <p className="text-[15px] font-sans text-ink-soft mb-4 leading-relaxed">Oahu travel tips and exclusive direct-booking offers, sent now and then.</p>
      <div className="flex w-full flex-col lg:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          aria-label="Email address for Oahu travel tips and offers"
          required
          className="flex-1 min-w-0 text-[15px] font-sans text-ink px-4 py-3 rounded-lg border border-sand-deep bg-shell focus:outline-none focus:border-ink focus:ring-1 focus:ring-ink transition-colors placeholder:text-driftwood"
          data-testid="footer-newsletter-email"
        />
        <button
          type="submit"
          disabled={status === "sending"}
          aria-busy={status === "sending" || undefined}
          className="inline-flex justify-center items-center gap-2 disabled:opacity-70 active:scale-[0.98] font-sans font-semibold text-[13px] tracking-[0.08em] uppercase px-6 py-3 rounded-lg bg-ink text-shell hover:bg-[#0f2513] hover:-translate-y-0.5 transition-all shadow-[0_3px_14px_rgba(0,0,0,0.12)] whitespace-nowrap"
          data-testid="footer-newsletter-submit"
        >
          {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          {status === "sending" ? "Sending" : "Subscribe"}
        </button>
      </div>
      {status === "error" && (
        <p role="alert" className="text-sm font-sans text-red-700 mt-2">We couldn't confirm your signup. Please try again.</p>
      )}
    </form>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();
  const { propertyRoutes, portfolioHomepage, publishedHomeCount } = useFeatureFlags();
  const stayHref = propertyRoutes ? KUALOA_STAY_PATH : "/stay";
  const bookCta = useBookCta();
  const bookHref = bookCta.href;
  const reviewsHref = propertyRoutes ? `${KUALOA_STAY_PATH}/reviews` : "/reviews";
  const galleryHref = propertyRoutes ? `${KUALOA_STAY_PATH}#gallery` : "/stay#gallery";
  const isPortfolioBrand = portfolioHomepage && publishedHomeCount > 1;

  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ['/api/settings/site_info'],
    retry: false,
  });

  return (
    <footer 
      id="footer"
      className="bg-shell border-t border-sand-deep py-16 lg:py-24"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-12 md:gap-x-10 lg:gap-x-16">

          {/* Brand & Newsletter */}
          <div className="md:col-span-5">
            <div className="mb-6">
              <BrandLockup variant="ink" size="md" brand={isPortfolioBrand ? "portfolio" : "kualoa"} />
            </div>
            <p className="text-[15px] font-sans text-ink-soft leading-relaxed max-w-sm mb-8">
              {isPortfolioBrand
                ? "Beachfront vacation homes in Ka'a'awa on Oahu's windward coast, each booked direct with the hosts: no platform fees, instant confirmation, personal support."
                : (siteSettings?.site_description || 'Your perfect vacation rental experience.')}
            </p>
            <FooterNewsletter />
          </div>
          
          {/* Quick Links */}
          <div className="md:col-span-2 pt-8 md:pt-0 border-t md:border-t-0 border-sand-deep">
            <h3 className="font-sans font-normal text-[11px] tracking-[0.3em] uppercase text-forest mb-6">Quick Links</h3>
            <ul className="space-y-2">
              <li><FooterLink href="/" testId="footer-home">Home</FooterLink></li>
              <li>{isPortfolioBrand ? <FooterLink href="/properties" testId="footer-stay">Our Homes</FooterLink> : <FooterLink href={stayHref} testId="footer-stay">Stay</FooterLink>}</li>
              <li><FooterLink href="/experiences" testId="footer-experiences">Experiences</FooterLink></li>
              <li><FooterLink href={bookHref} testId="footer-book">{bookCta.label}</FooterLink></li>
              <li><FooterLink href={reviewsHref} testId="footer-reviews">Guest Reviews</FooterLink></li>
              <li><FooterLink href={galleryHref} testId="footer-gallery">Gallery</FooterLink></li>
              <li><FooterLink href="/contact" testId="footer-contact">Contact</FooterLink></li>
            </ul>
          </div>
          
          {/* Oahu Travel Guide */}
          <div className="md:col-span-3 pt-8 md:pt-0 border-t md:border-t-0 border-sand-deep">
            <h3 className="font-sans font-normal text-[11px] tracking-[0.3em] uppercase text-forest mb-6">Oahu Guide</h3>
            <ul className="space-y-2">
              <li><FooterLink href="/faq" testId="footer-faq">FAQ</FooterLink></li>
              <li><FooterLink href="/blog" testId="footer-travel-guide">Vacation Guide</FooterLink></li>
              <li><FooterLink href="/blog/category/beaches-coastal-guides" testId="footer-beaches">Beaches Guide</FooterLink></li>
              <li><FooterLink href="/blog/category/food-local-culture" testId="footer-food-culture">Food & Culture</FooterLink></li>
              <li><FooterLink href="/blog/category/hiking-outdoor-adventure" testId="footer-hiking">Hiking Trails</FooterLink></li>
              <li><FooterLink href="/blog/category/transportation-practical-guides" testId="footer-transportation">Travel Tips</FooterLink></li>
            </ul>
          </div>
          
          {/* Connect & Policies */}
          <div className="md:col-span-2 pt-8 md:pt-0 border-t md:border-t-0 border-sand-deep">
            <h3 className="font-sans font-normal text-[11px] tracking-[0.3em] uppercase text-forest mb-6">Connect</h3>
            <ContactActionLinks
              phone={siteSettings?.contact_phone}
              email={siteSettings?.contact_email}
              variant="links"
              className="flex-col items-start gap-3 mb-10"
              testIdPrefix="footer"
            />

            <h3 className="font-sans font-normal text-[11px] tracking-[0.3em] uppercase text-forest mb-6">Policies</h3>
            <ul className="space-y-2">
              <li><FooterLink href="/policies#terms" testId="footer-terms">Terms</FooterLink></li>
              <li><FooterLink href="/policies#privacy" testId="footer-privacy">Privacy</FooterLink></li>
              {/* Points at the human-readable sitemap, not sitemap.xml. The XML
                  file is for crawlers that already know to ask for it; this link
                  is the one a person - or a crawler following links - can use,
                  and it is what makes every guide reachable from any page. */}
              <li><FooterLink href="/sitemap" testId="footer-sitemap">Sitemap</FooterLink></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-sand-deep mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left">
          <p className="text-[13px] font-sans text-driftwood">
            &copy; {currentYear} {isPortfolioBrand ? 'Vacation Rental Oahu' : (siteSettings?.site_name || 'The Kualoa Beach House')}. All rights reserved.
          </p>
          <div className="text-[11px] font-sans text-driftwood uppercase tracking-[0.3em] flex items-center gap-3">
            <span>Oahu</span>
            <span className="w-1 h-1 rounded-full bg-sand-deep"></span>
            <span>Hawaii</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
